import { useCallback } from "react";
import { useGlobalContext } from "../Context/GlobalContext";

const normalizeString = (value) => String(value ?? "").trim();

const useCoupon = () => {
  const { api } = useGlobalContext();

  // ============================================
  // GET COUPON CODES
  // ============================================
  const getCouponCodes = useCallback(
    async ({
      amount = 0,
      orderId = "",
      orderType = "home_delivery",
      userid = "",
    } = {}) => {
      try {
        const payload = {
          amount: String(amount || "0"),
          orderId: normalizeString(orderId),
          orderType: normalizeString(orderType),
          userid: normalizeString(userid),
        };

        console.log("===== getCouponCodes Payload =====");
        console.log(payload);

        const response = await api.post("/Order/v1.0/getCouponCodes", payload);

        const responseData = response?.data;

        console.log("===== getCouponCodes Response =====");
        console.log(responseData);

        if (String(responseData?.responseCode) !== "00") {
          throw new Error(
            responseData?.responseMessage || "Unable to fetch coupons.",
          );
        }

        return {
          success: true,
          coupons: Array.isArray(responseData?.response)
            ? responseData.response
            : [],
          message: responseData?.responseMessage || "",
          raw: responseData,
        };
      } catch (error) {
        console.error("getCouponCodes error:", error);

        throw new Error(
          error?.response?.data?.responseMessage ||
            error?.message ||
            "Unable to fetch coupons.",
        );
      }
    },
    [api],
  );

  // ============================================
  // APPLY COUPON
  // ============================================
  const applyCouponCode = useCallback(
    async ({ couponCode = "", orderid = "" } = {}) => {
      try {
        const cleanCouponCode = normalizeString(couponCode);
        const cleanOrderId = normalizeString(orderid);

        if (!cleanCouponCode) {
          throw new Error("Coupon code is required.");
        }

        if (!cleanOrderId) {
          throw new Error("Order ID is required.");
        }

        const payload = {
          couponCode: cleanCouponCode,
          orderid: cleanOrderId,
        };

        console.log("===== applyCouponCode Payload =====");
        console.log(payload);

        const response = await api.post("/Order/v1.0/applyCouponCode", payload);

        const responseData = response?.data;

        console.log("===== applyCouponCode Response =====");
        console.log(responseData);

        if (String(responseData?.responseCode) !== "00") {
          throw new Error(
            responseData?.responseMessage || "Unable to apply coupon.",
          );
        }

        return {
          success: true,
          message: responseData?.responseMessage || "",
          data: responseData?.data || null,
          raw: responseData,
        };
      } catch (error) {
        console.error("applyCouponCode error:", error);

        throw new Error(
          error?.response?.data?.responseMessage ||
            error?.message ||
            "Unable to apply coupon.",
        );
      }
    },
    [api],
  );

  // ============================================
  // CALCULATE DELIVERY + DISCOUNT
  // ============================================
  const calculateDeliveryChargesAndDiscount = useCallback(
    async ({ orderId = "", orderType = "home_delivery" } = {}) => {
      try {
        const cleanOrderId = normalizeString(orderId);
        const cleanOrderType = normalizeString(orderType);

        if (!cleanOrderId) {
          throw new Error("Order ID is required.");
        }

        const payload = {
          orderId: cleanOrderId,
          orderType: cleanOrderType,
        };

        console.log("===== calculateDeliveryChargesAndDiscount Payload =====");
        console.log(payload);

        const response = await api.post(
          "/Order/v1.0/calculateDeliveryChargesAndDiscount",
          payload,
        );

        const responseData = response?.data;

        console.log("===== calculateDeliveryChargesAndDiscount Response =====");
        console.log(responseData);

        if (String(responseData?.responseCode) !== "00") {
          throw new Error(
            responseData?.responseMessage ||
              "Unable to calculate order amount.",
          );
        }

        return {
          success: true,
          calculation: responseData?.response || null,
          message: responseData?.responseMessage || "",
          raw: responseData,
        };
      } catch (error) {
        console.error("calculateDeliveryChargesAndDiscount error:", error);

        throw new Error(
          error?.response?.data?.responseMessage ||
            error?.message ||
            "Unable to calculate order amount.",
        );
      }
    },
    [api],
  );

  return {
    getCouponCodes,
    applyCouponCode,
    calculateDeliveryChargesAndDiscount,
  };
};

export default useCoupon;
